import { Breadcrumb, BreadcrumbLink, BreadcrumbPage } from '@ads/components-react';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/shared/ui/organisms/PageHeader';

import { ClassPlanningStatusLegend } from '../../molecules/ClassPlanningStatusLegend';

export interface ClassPlanningListTemplateProps {
  /** Ação de exportação (botão "Exportar Excel") — exibida dentro do card de tabela, alinhada à
   * esquerda, acima da legenda (posição do legado — não no cabeçalho da página). */
  readonly exportAction?: ReactNode;
  /** Card de filtros. */
  readonly filterPanel: ReactNode;
  /** Tabela de planejamentos. */
  readonly table: ReactNode;
}

/**
 * Layout da tela de listagem de planejamento de aulas: breadcrumb, título (via
 * `PageHeader` shared, sem contagem no subtítulo), card de filtros e card de
 * tabela (com a ação de exportação e a legenda de status acima das linhas). Sem
 * dados/lógica — recebe tudo por slots. Espaçamento entre cards alinhado ao
 * `ExamListTemplate` (`/exams`) — ver `design.md` da change
 * `class-planning-listing-adjustments`.
 */
export function ClassPlanningListTemplate({
  exportAction,
  filterPanel,
  table,
}: ClassPlanningListTemplateProps) {
  const { t } = useTranslation('classPlanning');

  return (
    <div className="flex flex-col gap-nano">
      <PageHeader
        title={t('list.title')}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">{t('breadcrumb.home')}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('breadcrumb.list')}</BreadcrumbPage>
          </Breadcrumb>
        }
      />

      <div className="rounded-md border-hairline my-xxs border-ctx-layout-border bg-ctx-layout-surface p-sm shadow-level1">
        {filterPanel}
      </div>

      <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1">
        <div className="flex flex-col gap-xs p-xxs">
          {exportAction && <div className="flex justify-start">{exportAction}</div>}
          <ClassPlanningStatusLegend />
        </div>
        {table}
      </div>
    </div>
  );
}
